// src/components/GoalWizard.js
//
// Guided, step-by-step goal + plan setup for RESET Ultra. Replaces the old
// single-panel PlanGenerator UI. Pure presentation: collects the goal, any
// milestone races, and the training rhythm, then hands a payload to the parent
// via `onSubmit` (which runs the SAME server-side generator + readiness gate as
// before). On a readiness/pace block it shows the refusal + three honest
// options — never silently builds an injury-bait plan.
//
// Props:
//   onClose()                         — dismiss the wizard
//   onSubmit(payload, opts) -> result — create B goals + generate (preview).
//        payload: { distanceKm, distanceKey, terrain, mode, time, someday, raceDate,
//                   milestones:[{distanceKey, distanceKm, date, time}],
//                   runsPerWeek, days:[dowNumbers] }
//        opts:    { finishOnly?, acknowledgeReadiness?, skipGoals?,
//                   overrideDistanceKm?, overrideRaceDate? }
//        result:  { plan } | { blocked, reason, readiness, paceGuard } | null
//   onComplete(plan)                  — commit the previewed plan (parent).
//   prefill  { distanceKm, raceDate, targetFinish, terrain, runsPerWeek, days,
//              longDay, tempoDay }   — optional: start on the summary with an
//                                      existing goal (used when a rebuild
//                                      outside the wizard was refused).
//   initialGate  blocked result       — optional: open directly on the gate.
//                                      Leaving that gate closes the wizard and
//                                      keeps the current plan.

import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, AlertTriangle } from "lucide-react";
import { distKeyForKm, gateOptionsFor } from "../lib/readinessGate";

const DIST = {
  "5K": { label: "5K", km: 5, ultra: false },
  "10K": { label: "10K", km: 10, ultra: false },
  HM: { label: "Half", km: 21.0975, ultra: false },
  M: { label: "Marathon", km: 42.195, ultra: false },
  "50K": { label: "50K", km: 50, ultra: true },
  "100K": { label: "100K", km: 100, ultra: true },
  "100M": { label: "100M", km: 161, ultra: true },
  custom: { label: "Custom", km: 0, ultra: true },
};
const TERRAINS = [["road", "Road"], ["trail", "Trail"], ["mixed", "Mixed"], ["mountain", "Mountain"]];
const TERRAIN_LABEL = { road: "Road", trail: "Trail", mixed: "Mixed", mountain: "Mountain" };
const TERRAIN_HINT = {
  road: "Flat and paced. No elevation targets, no hill sessions.",
  trail: "Elevation targets and hill work in the build. Paces read slower.",
  mixed: "Some climbing. Lighter elevation targets than trail.",
  mountain: "Big vertical. Hiking is part of the specific phase.",
};
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DOW = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TOTAL = 6; // 5 steps + summary
const ITEMH = 46;

const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
const fmtDate = (s) => (s ? new Date(s + "T00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
const fmtDateLong = (s) => (s ? new Date(s + "T00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—");
const parseTime = (str) => {
  const p = String(str).split(":").map(Number);
  if (p.length === 3) return { h: p[0], m: p[1], s: p[2] };
  if (p.length === 2) return { h: 0, m: p[0], s: p[1] };
  return { h: 0, m: 0, s: 0 };
};
const DOW_NAME = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };

function defaultTime(km) {
  if (km >= 45) return { h: 12, m: 0, s: 0 };
  if (km >= 42) return { h: 3, m: 30, s: 0 };
  if (km >= 21) return { h: 1, m: 45, s: 0 };
  if (km >= 10) return { h: 0, m: 45, s: 0 };
  return { h: 0, m: 22, s: 0 };
}

/* ---------- scroll-wheel column ---------- */
function Wheel({ max, initial, colRef, onSettle }) {
  const ref = useRef(null);
  useEffect(() => {
    if (colRef) colRef.current = ref.current;
    if (ref.current) {
      requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.scrollTop = initial * ITEMH;
          mark();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const mark = () => {
    const el = ref.current;
    if (!el) return;
    const idx = Math.max(0, Math.round(el.scrollTop / ITEMH));
    el.querySelectorAll(".gwWi").forEach((c, i) => c.classList.toggle("mid", i === idx));
    if (onSettle) onSettle();
  };
  const tRef = useRef(null);
  const onScroll = () => {
    mark();
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.max(0, Math.round(el.scrollTop / ITEMH));
      const target = idx * ITEMH;
      if (Math.abs(el.scrollTop - target) > 1) el.scrollTo({ top: target, behavior: "smooth" });
    }, 90);
  };
  const items = [];
  for (let i = 0; i <= max; i++) items.push(<div className="gwWi" key={i}>{pad(i)}</div>);
  return (
    <div className="gwWcol" ref={ref} onScroll={onScroll}>
      {items}
    </div>
  );
}

export default function GoalWizard({ onClose, onSubmit, onComplete, prefill = null, initialGate = null }) {
  const pf = prefill && prefill.distanceKm ? prefill : null;
  const pfKey = pf ? distKeyForKm(pf.distanceKm) : null;
  const [step, setStep] = useState(pf ? TOTAL - 1 : 0);
  const [dist, setDist] = useState(pfKey);
  // The surface. Until the athlete picks one it follows the distance card
  // (ultra cards suggest trail, road cards road); once picked it stays.
  const [terrain, setTerrain] = useState(pf && pf.terrain ? pf.terrain : null);
  const [customKm, setCustomKm] = useState(pfKey === "custom" ? String(pf.distanceKm) : "");
  const [mode, setMode] = useState(pf ? (pf.targetFinish ? "time" : "finish") : null); // 'finish' | 'time'
  const [time, setTime] = useState(pf && pf.targetFinish ? pf.targetFinish : "");
  const [someday, setSomeday] = useState(false);
  const [raceDate, setRaceDate] = useState(pf ? pf.raceDate || "" : "");
  const [miles, setMiles] = useState([]);
  const [rpw, setRpw] = useState(pf && pf.runsPerWeek ? pf.runsPerWeek : 4);
  const [days, setDays] = useState(pf && pf.days && pf.days.length ? pf.days.map((d) => DOW_NAME[d]).filter(Boolean) : ["Tue", "Thu", "Sat", "Sun"]);
  const [longDay, setLongDay] = useState(pf && pf.longDay != null ? DOW_NAME[pf.longDay] || null : null);
  const [tempoDay, setTempoDay] = useState(pf && pf.tempoDay != null ? DOW_NAME[pf.tempoDay] || null : null);
  // mini add
  const [showMini, setShowMini] = useState(false);
  const [mini, setMini] = useState({ dist: null, date: "", time: "" });
  // sheets
  const [timeSheet, setTimeSheet] = useState(false);
  const [timeCtx, setTimeCtx] = useState("main");
  const [timeRead, setTimeRead] = useState("0:00:00");
  const [dateSheet, setDateSheet] = useState(false);
  const [dateCtx, setDateCtx] = useState("main");
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [calSel, setCalSel] = useState(null);
  // build
  const [building, setBuilding] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");
  const [gate, setGate] = useState(initialGate || null); // blocked result
  const [err, setErr] = useState("");

  const wH = useRef(null), wM = useRef(null), wS = useRef(null);
  const showHours = useRef(true);

  const distKm = useCallback(() => {
    if (!dist) return 100;
    if (dist === "custom") return Number(customKm) || 100;
    return DIST[dist].km;
  }, [dist, customKm]);

  /* ---------- time sheet ---------- */
  const ctxKm = () => (timeCtx === "mile" ? (mini.dist ? DIST[mini.dist].km : 42) : distKm());
  const effTerrain = () => terrain || (dist === "custom" ? (distKm() > 42.3 ? "trail" : "road") : dist && DIST[dist] && DIST[dist].ultra ? "trail" : "road");
  const ctxLabel = () => (timeCtx === "mile" ? (mini.dist ? DIST[mini.dist].label : "race") : dist === "custom" ? distKm() + "K" : dist ? DIST[dist].label : "goal");
  const openTime = (ctx) => { setTimeCtx(ctx); setTimeSheet(true); };
  useEffect(() => {
    if (!timeSheet) return;
    const km = ctxKm();
    showHours.current = km >= 21;
    const cur = timeCtx === "mile" ? mini.time : time;
    const v = cur ? parseTime(cur) : defaultTime(km);
    setTimeRead(showHours.current ? `${v.h}:${pad(v.m)}:${pad(v.s)}` : `${pad(v.m)}:${pad(v.s)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSheet]);
  const readWheels = () => {
    const g = (r) => (r.current ? Math.max(0, Math.round(r.current.scrollTop / ITEMH)) : 0);
    const m = g(wM), s = g(wS);
    return showHours.current ? `${g(wH)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };
  const onWheelSettle = () => setTimeRead(readWheels());
  const setTimeVal = () => {
    const val = readWheels();
    if (timeCtx === "mile") setMini((mm) => ({ ...mm, time: val }));
    else setTime(val);
    setTimeSheet(false);
  };
  const clearTimeVal = () => {
    if (timeCtx === "mile") setMini((mm) => ({ ...mm, time: "" }));
    else setTime("");
    setTimeSheet(false);
  };

  /* ---------- date sheet ---------- */
  const openDate = (ctx) => {
    setDateCtx(ctx);
    const cur = ctx === "mile" ? mini.date : raceDate;
    const sel = cur ? new Date(cur + "T00:00") : null;
    setCalSel(sel);
    const base = sel || new Date();
    setCalMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    setDateSheet(true);
  };
  const setDateVal = () => {
    if (!calSel) return;
    const iso = isoOf(calSel);
    if (dateCtx === "mile") setMini((mm) => ({ ...mm, date: iso }));
    else { setRaceDate(iso); setSomeday(false); }
    setDateSheet(false);
  };
  const clearDateVal = () => {
    if (dateCtx === "mile") setMini((mm) => ({ ...mm, date: "" }));
    setDateSheet(false);
  };
  const calNav = (dir) => setCalMonth((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1));
  const calCells = () => {
    const y = calMonth.getFullYear(), m = calMonth.getMonth();
    const startDow = (new Date(y, m, 1).getDay() + 6) % 7;
    const days_ = new Date(y, m + 1, 0).getDate();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const out = [];
    for (let i = 0; i < startDow; i++) out.push({ empty: true, k: "e" + i });
    for (let d = 1; d <= days_; d++) {
      const date = new Date(y, m, d);
      const iso = isoOf(date);
      out.push({ d, iso, past: date < today, sel: calSel && iso === isoOf(calSel), today: iso === isoOf(today) });
    }
    for (let i = startDow + days_; i < 42; i++) out.push({ empty: true, k: "t" + i });
    return out;
  };

  /* ---------- milestones ---------- */
  const openMini = () => { setMini({ dist: null, date: "", time: "" }); setShowMini(true); };
  const saveMini = () => {
    if (!mini.dist) return;
    const next = [...miles, { ...mini, distanceKm: DIST[mini.dist].km }].sort((a, b) => ((a.date || "9") < (b.date || "9") ? -1 : 1));
    setMiles(next);
    setShowMini(false);
  };
  const delMile = (i) => setMiles(miles.filter((_, x) => x !== i));

  /* ---------- validation / nav ---------- */
  const valid = (st) => {
    if (st === 0) return !!dist && (dist !== "custom" || Number(customKm) > 0);
    if (st === 1) return mode === "finish" || (mode === "time" && !!time);
    if (st === 2) return someday || !!raceDate;
    return true;
  };
  const next = () => { if (step === TOTAL - 1) { build(); return; } if (!valid(step)) return; setStep(step + 1); };
  const back = () => { if (step === 0) { onClose(); return; } setStep(step - 1); };

  /* ---------- build ---------- */
  const payload = () => ({
    distanceKey: dist,
    distanceKm: distKm(),
    terrain: effTerrain(),
    mode,
    time,
    someday,
    raceDate,
    milestones: miles.map((m) => ({ distanceKey: m.dist, distanceKm: DIST[m.dist].km, date: m.date, time: m.time })),
    runsPerWeek: rpw,
    days: days.map((d) => DOW[d]),
    longDay: longDay ? DOW[longDay] : null,
    tempoDay: tempoDay ? DOW[tempoDay] : null,
  });
  const runBuild = async (opts = {}) => {
    setErr("");
    setBuilding(true);
    setDoneMsg(mode === "time" ? "Reading your Strava history and pacing the weeks to your target time." : "Reading your Strava history and laying out the weeks to your goal.");
    setDone(true);
    try {
      const res = await onSubmit(payload(), opts);
      if (res && res.blocked) { setDone(false); setGate(res); return; }
      if (res && res.plan) {
        if (onComplete) onComplete(res.plan);
        setDoneMsg("Your plan is ready.");
        setTimeout(() => onClose(), 1400);
        return;
      }
      // no plan (e.g. ambition without a date) — goals were still saved
      setDoneMsg(someday && !raceDate && miles.every((m) => !m.date)
        ? "Goal saved. Add a date to a race and we'll build the plan."
        : "Goal saved. Couldn't build the plan — try again from Settings.");
      setTimeout(() => onClose(), 1900);
    } catch (e) {
      setDone(false);
      setErr(e?.message || "Something went wrong. Try again.");
    } finally {
      setBuilding(false);
    }
  };
  const build = () => runBuild({});
  // Gate options act in ONE tap with the server's own suggestion (the
  // generator re-checks every retry — nothing here skips the gate except the
  // explicit "I've trained at this volume" override). Retries never re-post
  // milestone goals (skipGoals).
  const gateOpts = gateOptionsFor(gate);
  const gateFinishOnly = () => { setGate(null); runBuild({ finishOnly: true, acknowledgeReadiness: true, skipGoals: true }); };
  const gateLater = () => {
    const sug = gateOpts.laterDate;
    setGate(null);
    if (!sug) { setStep(2); return; }
    setSomeday(false);
    setRaceDate(sug);
    runBuild({ skipGoals: true, overrideRaceDate: sug });
  };
  const gateShorter = () => {
    const sd = gateOpts.shorterDistance;
    setGate(null);
    if (!sd) { setStep(0); return; }
    const key = sd.key;
    setDist(key);
    if (key === "custom") setCustomKm(String(sd.km));
    // A finish time entered for the longer race means nothing for this one.
    const dropTarget = mode === "time";
    if (dropTarget) { setMode("finish"); setTime(""); }
    runBuild({ skipGoals: true, overrideDistanceKm: sd.km, overrideNoTarget: dropTarget });
  };
  const gateAcknowledge = () => { setGate(null); runBuild({ skipGoals: true, acknowledgeReadiness: true }); };
  const gateAdjustTime = () => { setGate(null); setStep(1); };

  /* ---------- render helpers ---------- */
  const progress = [];
  for (let i = 0; i < 5; i++) progress.push(<i key={i} className={i < step ? "done" : i === step ? "now" : ""} />);
  const isSummary = step === TOTAL - 1;
  const nextLabel = isSummary ? "Build my plan" : step === 3 && miles.length === 0 ? "Skip — no races" : "Continue";

  const distCard = (key, group) => {
    const d = DIST[key];
    const on = dist === key;
    return (
      <button key={key} type="button" className={"gwDcard" + (on ? " on" : "")} onClick={() => { setDist(key); setTime(""); }}>
        {d.ultra && key !== "custom" && !on && <span className="gwTag">ULTRA</span>}
        {on && <svg className="gwCheck" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="var(--accent-base)" /><path d="M7 12.5l3 3 6-7" stroke="var(--content-on-fill)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
        <div className="gwDl">{d.label}</div>
        <div className="gwDk">{key === "custom" ? "Set your own" : (Number.isInteger(d.km) ? d.km : d.km.toFixed(1)) + " km"}</div>
      </button>
    );
  };

  return (
    <div className="gwRoot">
      <div className="gwTop">
        <div className="gwBrand">
          <span className="gwWord">RESET</span>
          <span className="gwBrand__right">
            <span className="gwStepno">{isSummary ? "Review" : `Step ${step + 1} of 5`}</span>
            <button type="button" className="gwClose" onClick={onClose} aria-label="Close without changing the plan"><X size={18} aria-hidden="true" /></button>
          </span>
        </div>
        <div className="gwProg">{progress}</div>
      </div>

      <div className="gwStage">
        {/* STEP 1 DISTANCE */}
        {step === 0 && (
          <div className="gwScreen">
            <div className="gwEye">Main goal</div>
            <h1 className="gwH1">How far do you want to go?</h1>
            <p className="gwSub">Your biggest target. It anchors the whole plan — everything else builds toward it.</p>
            <div className="gwGroup">Ultra</div>
            <div className="gwGrid">{["50K", "100K", "100M", "custom"].map((k) => distCard(k))}</div>
            <div className="gwGroup">Road</div>
            <div className="gwGrid">{["5K", "10K", "HM", "M"].map((k) => distCard(k))}</div>
            {dist === "custom" && (
              <div style={{ marginTop: 10 }}>
                <input className="gwInp" type="number" inputMode="numeric" min="1" max="400" placeholder="Distance in km" value={customKm} onChange={(e) => setCustomKm(e.target.value)} />
              </div>
            )}
            <div className="gwGroup">Surface</div>
            <div className="gwSurf" role="radiogroup" aria-label="Surface">
              {TERRAINS.map(([v, l]) => (
                <button type="button" key={v} role="radio" aria-checked={effTerrain() === v} className={"gwSurf__opt" + (effTerrain() === v ? " on" : "")} onClick={() => setTerrain(v)}>{l}</button>
              ))}
            </div>
            <p className="gwHint">{TERRAIN_HINT[effTerrain()]}{terrain ? "" : " Suggested from the distance; tap to change."}</p>
          </div>
        )}

        {/* STEP 2 MODE */}
        {step === 1 && (
          <div className="gwScreen">
            <div className="gwEye">Main goal</div>
            <h1 className="gwH1">What counts as done?</h1>
            <p className="gwSub">Cross the line, or chase a number. You can change your mind later.</p>
            <div className="gwChoices">
              <button type="button" className={"gwChoice" + (mode === "finish" ? " on" : "")} onClick={() => setMode("finish")}>
                <span className="gwCi">🏁</span><span><span className="gwCt">Just finish</span><span className="gwCs">Getting there is the win. No clock pressure.</span></span>
              </button>
              <button type="button" className={"gwChoice" + (mode === "time" ? " on" : "")} onClick={() => setMode("time")}>
                <span className="gwCi">⏱</span><span><span className="gwCt">Target time</span><span className="gwCs">You've got a number in mind and want the pace for it.</span></span>
              </button>
            </div>
            {mode === "time" && (
              <div className="gwField">
                <label className="gwFl">Target time</label>
                <button type="button" className={"gwPill" + (time ? " set" : "")} onClick={() => openTime("main")}>
                  <span className={time ? "gwPv" : "gwPe"}>{time || "Set your target time"}</span><span className="gwPedit">Edit</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 WHEN */}
        {step === 2 && (
          <div className="gwScreen">
            <div className="gwEye">Main goal</div>
            <h1 className="gwH1">When's the race?</h1>
            <p className="gwSub">A date sets the countdown and the taper. No date yet? Keep it as an ambition.</p>
            <div className="gwField">
              <label className="gwFl">Race date</label>
              <button type="button" className={"gwPill gwDatep" + (raceDate ? " set" : "") + (someday ? " off" : "")} disabled={someday} onClick={() => openDate("main")}>
                <span className={raceDate ? "gwPv" : "gwPe"}>{raceDate ? fmtDate(raceDate) : "Pick a date"}</span><span className="gwPedit">📅</span>
              </button>
            </div>
            <button type="button" className={"gwTog" + (someday ? " on" : "")} onClick={() => setSomeday(!someday)}>
              <span className="gwDot"><svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-6" stroke="var(--content-on-fill)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
              No date yet — it's a long-term ambition
            </button>
          </div>
        )}

        {/* STEP 4 MILESTONES */}
        {step === 3 && (
          <div className="gwScreen">
            <div className="gwEye">Stepping stones</div>
            <h1 className="gwH1">Any races on the way?</h1>
            <p className="gwSub">Shorter races before the big one sharpen you up. No date? We’ll suggest one and slot it into your plan — move or remove it any time.</p>
            <div className="gwMiles">
              {miles.map((m, i) => (
                <div className="gwMrow" key={i}>
                  <span className="gwMp">{DIST[m.dist].label.slice(0, 3)}</span>
                  <span className="gwMt"><b>{DIST[m.dist].label === "Half" ? "Half Marathon" : DIST[m.dist].label}</b><span className="gwMm">{[m.date ? fmtDate(m.date) : "no date", m.time ? "· " + m.time : ""].join(" ")}</span></span>
                  <button type="button" className="gwMx" onClick={() => delMile(i)}>×</button>
                </div>
              ))}
            </div>
            {!showMini && <button type="button" className="gwAdd" onClick={openMini}>＋ Add a race</button>}
            {showMini && (
              <div className="gwMini">
                <div className="gwChiprow">
                  {["5K", "10K", "HM", "M", "50K"].map((k) => (
                    <button type="button" key={k} className={"gwChip" + (mini.dist === k ? " on" : "")} onClick={() => setMini((mm) => ({ ...mm, dist: k }))}>{DIST[k].label}</button>
                  ))}
                </div>
                <div className="gwTwo">
                  <button type="button" className={"gwPill gwMiniP gwDatep" + (mini.date ? " set" : "")} onClick={() => openDate("mile")}><span className={mini.date ? "gwPv" : "gwPe"}>{mini.date ? fmtDate(mini.date) : "Date (optional)"}</span><span className="gwPedit">📅</span></button>
                  <button type="button" className={"gwPill gwMiniP" + (mini.time ? " set" : "")} onClick={() => openTime("mile")}><span className={mini.time ? "gwPv" : "gwPe"}>{mini.time || "Time (optional)"}</span><span className="gwPedit">⏱</span></button>
                </div>
                <div className="gwMinibtns">
                  <button type="button" className="gwBs gh" onClick={() => setShowMini(false)}>Cancel</button>
                  <button type="button" className="gwBs ok" onClick={saveMini}>Add race</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 5 RHYTHM */}
        {step === 4 && (
          <div className="gwScreen">
            <div className="gwEye">Training rhythm</div>
            <h1 className="gwH1">How often can you run?</h1>
            <p className="gwSub">Pick a weekly count and the days that actually work. We build around your life, not the other way round.</p>
            <div className="gwField">
              <label className="gwFl">Runs per week</label>
              <div className="gwSegrow">
                {[3, 4, 5].map((n) => (
                  <button type="button" key={n} className={"gwSeg" + (rpw === n ? " on" : "")} onClick={() => setRpw(n)}>{n}</button>
                ))}
              </div>
            </div>
            <div className="gwField">
              <label className="gwFl">Preferred days</label>
              <div className="gwDays">
                {DAYS.map((d) => (
                  <button type="button" key={d} className={"gwDay" + (days.includes(d) ? " on" : "")} onClick={() => setDays((cur) => { const nx = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]; if (!nx.includes(d)) { if (longDay === d) setLongDay(null); if (tempoDay === d) setTempoDay(null); } return nx; })}>{d[0]}</button>
                ))}
              </div>
            </div>
            <div className="gwField">
              <label className="gwFl">Preferred sessions <span className="gwOpt">optional</span></label>
              <div className="gwRole">
                <span className="gwRoleName">Long run</span>
                <div className="gwRoleDays">
                  {DAYS.filter((d) => days.includes(d)).map((d) => (
                    <button type="button" key={d} className={"gwRoleChip" + (longDay === d ? " on" : "")} onClick={() => setLongDay(longDay === d ? null : d)}>{d[0]}</button>
                  ))}
                  <button type="button" className={"gwRoleChip gwAuto" + (longDay === null ? " on" : "")} onClick={() => setLongDay(null)}>Auto</button>
                </div>
              </div>
              <div className="gwRole">
                <span className="gwRoleName">Tempo</span>
                <div className="gwRoleDays">
                  {DAYS.filter((d) => days.includes(d)).map((d) => (
                    <button type="button" key={d} className={"gwRoleChip" + (tempoDay === d ? " on" : "")} onClick={() => setTempoDay(tempoDay === d ? null : d)}>{d[0]}</button>
                  ))}
                  <button type="button" className={"gwRoleChip gwAuto" + (tempoDay === null ? " on" : "")} onClick={() => setTempoDay(null)}>Auto</button>
                </div>
              </div>
            </div>
            <p className="gwHint">Soft preferences — the generator keeps tempo at least 2 days from your long run, so it may nudge these. Easy runs fill the rest.</p>
          </div>
        )}

        {/* SUMMARY */}
        {step === 5 && (
          <div className="gwScreen">
            <div className="gwEye">Your plan</div>
            <h1 className="gwH1" style={{ marginBottom: 16 }}>Here's the shape of it.</h1>
            <div className="gwHero">
              <div className="gwHl">Main goal</div>
              <div className="gwHbig">{dist === "custom" ? (customKm || "?") + " km" : DIST[dist].label}</div>
              <div className="gwHmeta">
                <div><span className="gwK">Goal</span><span className="gwV">{mode === "time" ? time || "Target time" : "Finish"}</span></div>
                <div><span className="gwK">When</span><span className="gwV">{someday ? "Ambition" : raceDate ? fmtDate(raceDate) : "—"}</span></div>
                <div><span className="gwK">Surface</span><span className="gwV">{TERRAIN_LABEL[effTerrain()]}</span></div>
              </div>
            </div>
            {miles.length > 0 && (
              <div className="gwSblock">
                <div className="gwSh">Milestones on the way</div>
                {miles.map((m, i) => (
                  <div className="gwSline" key={i}>
                    <span className="gwSp">{DIST[m.dist].label.slice(0, 3)}</span>
                    <span className="gwSt"><b>{DIST[m.dist].label === "Half" ? "Half Marathon" : DIST[m.dist].label}</b><span className="gwSm">{[m.date ? fmtDate(m.date) : "no date", m.time ? "· " + m.time : ""].join(" ")}</span></span>
                  </div>
                ))}
              </div>
            )}
            <div className="gwSblock">
              <div className="gwSh">Training rhythm</div>
              <div className="gwSched"><b>{rpw} runs</b> a week — {DAYS.filter((d) => days.includes(d)).join(", ") || "no days yet"}.</div>
            </div>
            {err && <div className="gwErr">{err}</div>}
            <div style={{ height: 10 }} />
          </div>
        )}
      </div>

      <div className="gwFoot">
        <button type="button" className="gwBtn ghost" onClick={back}>{step === 0 ? "✕" : "‹"}</button>
        <button type="button" className="gwBtn prim" disabled={!valid(step) || building} onClick={next}>{nextLabel}</button>
      </div>

      {/* TIME SHEET */}
      {timeSheet && (
        <>
          <div className="gwBack show" onClick={() => setTimeSheet(false)} />
          <div className="gwSheet show">
            <div className="gwGrip" />
            <div className="gwShTitle">{timeCtx === "mile" ? "Race time" : "Target time"}</div>
            <div className="gwShRead">{timeRead}</div>
            <div className="gwShCtx">for your {ctxLabel()}{timeCtx === "mile" ? " · optional" : ""}</div>
            <div className="gwWheels">
              <div className="gwWband" style={{ width: (showHours.current ? 206 : 134) + "px" }} />
              {showHours.current && <Wheel max={ctxKm() >= 45 ? 60 : 12} initial={(timeCtx === "mile" ? (mini.time ? parseTime(mini.time) : defaultTime(ctxKm())) : (time ? parseTime(time) : defaultTime(ctxKm()))).h} colRef={wH} onSettle={onWheelSettle} />}
              {showHours.current && <div className="gwWsep">:</div>}
              <Wheel max={59} initial={(timeCtx === "mile" ? (mini.time ? parseTime(mini.time) : defaultTime(ctxKm())) : (time ? parseTime(time) : defaultTime(ctxKm()))).m} colRef={wM} onSettle={onWheelSettle} />
              <div className="gwWsep">:</div>
              <Wheel max={59} initial={(timeCtx === "mile" ? (mini.time ? parseTime(mini.time) : defaultTime(ctxKm())) : (time ? parseTime(time) : defaultTime(ctxKm()))).s} colRef={wS} onSettle={onWheelSettle} />
            </div>
            <div className="gwWunit">{showHours.current && <><span>hrs</span><span className="sep" /></>}<span>min</span><span className="sep" /><span>sec</span></div>
            <button type="button" className="gwShSet" onClick={setTimeVal}>Set time</button>
            <button type="button" className="gwShClear" onClick={clearTimeVal}>{timeCtx === "mile" ? "No time — just finish" : "Clear"}</button>
          </div>
        </>
      )}

      {/* DATE SHEET */}
      {dateSheet && (
        <>
          <div className="gwBack show" onClick={() => setDateSheet(false)} />
          <div className="gwSheet show">
            <div className="gwGrip" />
            <div className="gwShTitle">Race date</div>
            <div className="gwShRead gwDateRead">{calSel ? fmtDateLong(isoOf(calSel)) : "—"}</div>
            <div className="gwShCtx">{dateCtx === "mile" ? "when is this race? · optional" : "pick your race day"}</div>
            <div className="gwCal">
              <div className="gwCalNav"><button type="button" onClick={() => calNav(-1)}>‹</button><div className="gwCalMonth">{MONTHS[calMonth.getMonth()]} {calMonth.getFullYear()}</div><button type="button" onClick={() => calNav(1)}>›</button></div>
              <div className="gwCalWd"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div>
              <div className="gwCalGrid">
                {calCells().map((c, i) => c.empty ? <button key={c.k || i} className="gwCell empty" /> : (
                  <button key={c.iso} type="button" className={"gwCell" + (c.past ? " dim" : "") + (c.sel ? " sel" : "") + (c.today ? " today" : "")} disabled={c.past} onClick={() => setCalSel(new Date(c.iso + "T00:00"))}>{c.d}</button>
                ))}
              </div>
            </div>
            <button type="button" className="gwShSet" disabled={!calSel} onClick={setDateVal}>Set date</button>
            {dateCtx === "mile" && <button type="button" className="gwShClear" onClick={clearDateVal}>No date</button>}
          </div>
        </>
      )}

      {/* READINESS GATE */}
      {gate && (
        <div className="gwGate">
          <div className="gwGateInner">
            <div className="gwGateIcon" aria-hidden="true"><AlertTriangle size={32} /></div>
            <h2 className="gwGateH">{gate.reason === "pace-guard"
              ? "That target pace is out of reach for now."
              : gate.readiness && gate.readiness.noBaseVolume
                ? "Build a base first."
                : "That plan would be an injury risk."}</h2>
            <p className="gwGateP">
              {gate.reason === "pace-guard"
                ? (gateOpts.fixManualPace
                  ? "The threshold pace you set in Settings is well faster than your recent runs. Every training pace would be built from it. Change it or clear it in Goal & plan settings, then build again."
                  : "Your target pace is a big jump from where you are now. Building straight to it invites injury.")
                : gate.readiness && gate.readiness.baseNote
                  ? gate.readiness.baseNote
                : gate.readiness && Number(gate.readiness.baselineWeeklyKm) > 0
                  ? `Your last four weeks come to about ${gate.readiness.baselineWeeklyKm} km/wk. A safe ramp to this goal would need to start near ${Math.round(gate.readiness.requiredStart || 0)} km/wk and peak near ${Math.round(gate.readiness.peakKm || 0)} km/wk.`
                  : gate.readiness
                    ? `Your last four weeks show no running. A safe ramp to this goal would need to start near ${Math.round(gate.readiness.requiredStart || 0)} km/wk.`
                    : "This goal needs more runway than the timeline allows."}
            </p>
            <div className="gwGateOpts">
              {gateOpts.acknowledge && (
                <button type="button" className="gwGateOpt gwGateOpt--plain" onClick={gateAcknowledge}><b>I've trained at this volume before</b><span>Build the full plan anyway. Your choice is recorded, and the plan keeps flagging the stretch.</span></button>
              )}
              {gateOpts.adjustTime && (
                <button type="button" className="gwGateOpt" onClick={gateAdjustTime}><b>Adjust the target time</b><span>Pick a time closer to what your recent runs support.</span></button>
              )}
              {gateOpts.laterDate && (
                <button type="button" className="gwGateOpt" onClick={gateLater}><b>Give it more time</b><span>Move the race to {fmtDate(gateOpts.laterDate)} so you can ramp up safely.</span></button>
              )}
              {gateOpts.shorterDistance && (
                <button type="button" className="gwGateOpt" onClick={gateShorter}><b>Pick a shorter distance</b><span>Switch to {gateOpts.shorterDistance.label} on the same date{mode === "time" ? " (target time cleared)" : ""}.</span></button>
              )}
              {gateOpts.finishOnly && (
                <button type="button" className="gwGateOpt" onClick={gateFinishOnly}><b>Build a finish-only plan</b><span>An honest plan built to get you to the line: time on feet, walk-run, fuelling.</span></button>
              )}
            </div>
            {initialGate ? (
              <>
                <button type="button" className="gwGateKeep" onClick={onClose}>Keep my current plan</button>
                <button type="button" className="gwGateBack" onClick={() => setGate(null)}>Edit the goal instead</button>
              </>
            ) : (
              <button type="button" className="gwGateBack" onClick={() => setGate(null)}>Back</button>
            )}
          </div>
        </div>
      )}

      {/* DONE */}
      {done && (
        <div className="gwDone show">
          <div className="gwRing"><svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M4 12.5l5 5L20 6" stroke="var(--accent-base)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
          <h2>{doneMsg === "Your plan is ready." || doneMsg.startsWith("Goal saved") ? doneMsg : "Building your plan…"}</h2>
          {!(doneMsg === "Your plan is ready." || doneMsg.startsWith("Goal saved")) && <p>{doneMsg}</p>}
        </div>
      )}
    </div>
  );
}
